import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './NotificationsList.css';
import { NotificationRow } from "./NotificationRow";
import { Spinner } from 'reactstrap';
import { 
    FaBell, 
    FaExclamationTriangle, 
    FaSyncAlt, 
    FaInbox, 
    FaCalendarAlt,
    FaChevronDown
} from 'react-icons/fa';
import moment from 'moment';
import 'moment/locale/fr';

// Configure moment to use French locale
moment.locale('fr');

const NotificationsList = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all'); // 'all', 'unread', 'today'
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);

    // Track which notifications are new (for animation)
    const [newNotificationIds, setNewNotificationIds] = useState(new Set());

    // Fetch notifications
    const fetchNotifications = async (pageNumber = 1, append = false) => {
        try {
            const isFirstLoad = pageNumber === 1 && !append;
            
            if (isFirstLoad) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            
            // Call API to get notifications with pagination
            const response = await sdk.models.SocialNotification.myNotifications({
                page: pageNumber,
                limit: 10, // 10 items per page
                filter
            });
            
            // Update state based on whether we're appending or replacing
            if (append) {
                // Track new notifications for animation
                const currentIds = new Set(notifications.map(n => n.id));
                const newIds = response.filter(n => !currentIds.has(n.id)).map(n => n.id);
                
                setNotifications(prev => [...prev, ...response]);
                setNewNotificationIds(new Set(newIds));
            } else {
                setNotifications(response);
                // All are considered "new" for animation when first loading
                setNewNotificationIds(new Set(response.map(n => n.id)));
            }
            
            // Determine if there might be more notifications to load
            setHasMore(response.length === 10); // If we got a full page, assume there might be more
            
            // Mark notifications as seen after loading
            await sdk.models.SocialNotification.markSeen({});
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError('Impossible de charger vos notifications. Veuillez réessayer plus tard.');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchNotifications(1, false);
    }, [filter]);

    // Load more notifications
    const loadMoreNotifications = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchNotifications(nextPage, true);
    };

    // Refresh notifications
    const refreshNotifications = () => {
        setPage(1);
        fetchNotifications(1, false);
    };

    // Apply filter
    const handleFilterChange = (newFilter) => {
        if (newFilter !== filter) {
            setFilter(newFilter);
            setPage(1);
        }
    };

    // Group notifications by date
    const groupedNotifications = useMemo(() => {
        const today = moment().startOf('day');
        const yesterday = moment().subtract(1, 'days').startOf('day');
        const lastWeek = moment().subtract(7, 'days').startOf('day');
        
        const groups = {
            today: [],
            yesterday: [],
            thisWeek: [],
            earlier: []
        };
        
        notifications.forEach(notification => {
            const notifDate = moment.unix(notification.time);
            
            if (notifDate >= today) {
                groups.today.push(notification);
            } else if (notifDate >= yesterday) {
                groups.yesterday.push(notification);
            } else if (notifDate >= lastWeek) {
                groups.thisWeek.push(notification);
            } else {
                groups.earlier.push(notification);
            }
        });
        
        return groups;
    }, [notifications]);

    // Show appropriate content based on state
    const renderContent = () => {
        if (loading) {
            return (
                <div className="notilist-loading-container">
                    <Spinner color="primary" />
                </div>
            );
        }
        
        if (error) {
            return (
                <div className="notilist-error">
                    <FaExclamationTriangle className="notilist-error-icon" />
                    <span>{error}</span>
                </div>
            );
        }
        
        if (notifications.length === 0) {
            return (
                <div className="notilist-empty">
                    <div className="notilist-empty-icon">
                        <FaInbox />
                    </div>
                    <h3 className="notilist-empty-title">Aucune notification</h3>
                    <p className="notilist-empty-text">
                        Vous n'avez pas de notifications pour le moment.
                    </p>
                </div>
            );
        }
        
        return (
            <>
                <div className="notilist-items">
                    {groupedNotifications.today.length > 0 && (
                        <div className="notilist-section">
                            <div className="notilist-section-title">Aujourd'hui</div>
                            {groupedNotifications.today.map(notification => (
                                <NotificationRow 
                                    key={notification.id} 
                                    notification={notification}
                                    isNew={newNotificationIds.has(notification.id)}
                                />
                            ))}
                        </div>
                    )}
                    
                    {groupedNotifications.yesterday.length > 0 && (
                        <div className="notilist-section">
                            <div className="notilist-section-title">Hier</div>
                            {groupedNotifications.yesterday.map(notification => (
                                <NotificationRow 
                                    key={notification.id} 
                                    notification={notification}
                                    isNew={newNotificationIds.has(notification.id)}
                                />
                            ))}
                        </div>
                    )}
                    
                    {groupedNotifications.thisWeek.length > 0 && (
                        <div className="notilist-section">
                            <div className="notilist-section-title">Cette semaine</div>
                            {groupedNotifications.thisWeek.map(notification => (
                                <NotificationRow 
                                    key={notification.id} 
                                    notification={notification} 
                                    isNew={newNotificationIds.has(notification.id)}
                                />
                            ))}
                        </div>
                    )}
                    
                    {groupedNotifications.earlier.length > 0 && (
                        <div className="notilist-section">
                            <div className="notilist-section-title">Plus ancien</div>
                            {groupedNotifications.earlier.map(notification => (
                                <NotificationRow 
                                    key={notification.id} 
                                    notification={notification}
                                    isNew={newNotificationIds.has(notification.id)}
                                />
                            ))}
                        </div>
                    )}
                </div>
                
                {hasMore && (
                    <div className="notilist-load-more">
                        <button 
                            className="notilist-load-more-btn" 
                            onClick={loadMoreNotifications}
                            disabled={loadingMore}
                        >
                            {loadingMore ? (
                                <>
                                    <Spinner size="sm" />
                                    Chargement...
                                </>
                            ) : (
                                <>
                                    <FaChevronDown />
                                    Voir plus
                                </>
                            )}
                        </button>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="notilist-wrapper">
            <div className="notilist-header">
                <h1 className="notilist-title">
                    <FaBell className="notilist-title-icon" />
                    Notifications
                </h1>
                <div className="notilist-actions">
                    <div className="notilist-filter-group">
                        <button 
                            className={`notilist-filter-button ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('all')}
                        >
                            <FaBell />
                            Toutes
                        </button>
                        <button 
                            className={`notilist-filter-button ${filter === 'unread' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('unread')}
                        >
                            <FaInbox />
                            Non lues
                        </button>
                        <button 
                            className={`notilist-filter-button ${filter === 'today' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('today')}
                        >
                            <FaCalendarAlt />
                            Récentes
                        </button>
                    </div>
                    <button 
                        className="notilist-filter-button" 
                        onClick={refreshNotifications}
                        disabled={loading}
                    >
                        <FaSyncAlt />
                    </button>
                </div>
            </div>
            
            <div className="notilist-container">
                {renderContent()}
            </div>
        </div>
    );
};

export { NotificationsList };